import React, { useState, useEffect } from 'react';
import { FaSearch, FaTrashAlt, FaPen } from 'react-icons/fa';
import AddProject from './AddProject/AddProject';
import Loading from '../../../component/Loading/Loading';
import AlertMsg from '../../../component/AlertMsg/AlertMsg';
import GlassmorphicModal from '../../../component/ReusableComponents/GlassmorphicModal';

const Project = () => {
  const [projects, setProjects] = useState([]);
  const [showAddProject, setShowAddProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);

  // Load projects from localStorage on component mount
  useEffect(() => {
    const savedProjects = localStorage.getItem('projects');
    if (savedProjects) {
      try {
        const parsedProjects = JSON.parse(savedProjects);
        setProjects(parsedProjects);
      } catch (error) {
        console.error('Error parsing projects from localStorage:', error);
        // If parsing fails, initialize with default data
        initializeDefaultProjects();
      }
    } else {
      // If no saved projects, initialize with default data
      initializeDefaultProjects();
    }
  }, []);

  const initializeDefaultProjects = () => {
    const defaultProjects = [
      {
        id: 1,
        name: 'Project Alpha',
        vendor: 'Vendor A',
        vendorType: 'Contractor',
        vendorEmail: 'vendor.a@example.com',
        vendorContact: '1234567890',
        status: 'In Progress',
        startDate: '2025-01-01',
        endDate: '2025-06-01',
        description: 'Default project description for Project Alpha'
      },
      {
        id: 2,
        name: 'Project Beta',
        vendor: 'Vendor B',
        vendorType: 'Electrician',
        vendorEmail: 'vendor.b@example.com',
        vendorContact: '0987654321',
        status: 'To be Start',
        startDate: '2025-03-01',
        endDate: '2025-09-01',
        description: 'Default project description for Project Beta'
      },
    ];
    setProjects(defaultProjects);
    saveToLocalStorage(defaultProjects);
  };

  const saveToLocalStorage = (updatedProjects) => {
    try {
      localStorage.setItem('projects', JSON.stringify(updatedProjects));
      setProjects(updatedProjects);
    } catch (error) {
      console.error('Error saving projects to localStorage:', error);
      setAlert({ visible: true, message: 'Error saving data to localStorage!', type: 'error' });
    }
  };

  const handleAddProjectClick = () => {
    setEditingProject(null);
    setShowAddProject(true);
  };

  const handleCloseDrawer = () => {
    setShowAddProject(false);
    setEditingProject(null);
  };

  const handleSaveProject = (projectData) => {
    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      let updatedProjects;
      if (editingProject && editingProject.id) {
        // Edit existing project
        updatedProjects = projects.map((p) => (p.id === projectData.id ? { ...projectData } : p));
        setAlert({ visible: true, message: 'Project updated successfully!', type: 'success' });
      } else {
        // Add new project - generate unique ID
        const newId = projects.length > 0 ? Math.max(...projects.map(p => p.id)) + 1 : 1;
        const newProject = { ...projectData, id: newId };
        updatedProjects = [...projects, newProject];
        setAlert({ visible: true, message: 'Project added successfully!', type: 'success' });
      }
      saveToLocalStorage(updatedProjects);
      handleCloseDrawer();
      setLoading(false);
    }, 1000);
  };

  const handleEditClick = (project) => {
    setEditingProject(project);
    setShowAddProject(true);
  };

  const handleDeleteClick = (id) => {
    setProjectIdToDelete(id);
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setProjectIdToDelete(null);
  };

  const confirmDelete = () => {
    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      const updatedProjects = projects.filter((project) => project.id !== projectIdToDelete);
      saveToLocalStorage(updatedProjects);
      setAlert({ visible: true, message: 'Project deleted successfully!', type: 'success' });
      closeModal();
      setLoading(false);
    }, 500);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);
  const handleDateChange = (e) => setDateFilter(e.target.value);

  const getFilteredProjects = () => {
    return projects.filter((project) => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter ? project.status === statusFilter : true;
      const matchesDate = dateFilter
        ? project.startDate === dateFilter || project.endDate === dateFilter
        : true;
      return matchesSearch && matchesStatus && matchesDate;
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Projects</h1>
            <div className="flex items-center">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
                onClick={handleAddProjectClick}
              >
                + Add Project
              </button>
            </div>
          </div>

          {/* Projects Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="">
              <div className="flex items-center mb-4 p-4 gap-4 flex-wrap">
                <div className="relative w-full md:w-80">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSearch className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search Projects"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="w-full md:w-60">
                  <select
                    value={statusFilter}
                    onChange={handleStatusChange}
                    className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="To be Start">To be Start</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="w-full md:w-48">
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={handleDateChange}
                    className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-400">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Project Name
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Vendor Name
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Vendor Type
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredProjects().length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                          No Data Found
                        </td>
                      </tr>
                    ) : (
                      getFilteredProjects().map((project, index) => (
                        <tr key={project.id} className={index % 2 === 1 ? "bg-gray-50" : ""}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {project.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {project.vendor || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {project.vendorType || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${project.status === 'Completed'
                              ? "bg-green-100 text-green-800"
                              : project.status === 'In Progress'
                                ? "bg-blue-100 text-blue-800"
                                : "bg-yellow-100 text-yellow-800"
                              }`}>
                              {project.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {project.startDate}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {project.endDate}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              className="text-blue-500 hover:text-blue-700 mr-3"
                              onClick={() => handleEditClick(project)}
                              title="Edit Project"
                            >
                              <FaPen size={18} />
                            </button>
                            <button
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteClick(project.id)}
                              title="Delete Project"
                            >
                              <FaTrashAlt size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {getFilteredProjects().length} of {projects.length} projects
                </div>
                <div className="flex-1 flex justify-end">
                  {/* Add pagination component here if needed */}
                  <div className="text-sm text-gray-500">
                    {getFilteredProjects().length} results
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showAddProject && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[999]">
              <AddProject
                onClose={handleCloseDrawer}
                onAddProject={handleSaveProject}
                editingProject={editingProject}
              />
            </div>
          )}

          {/* Delete Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showDeleteModal}
            onClose={closeModal}
            title="Confirm Delete"
            size="sm"
            footer={
              <div className="flex justify-end space-x-2">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
              </div>
            }
          >
            <p className="text-gray-700">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default Project;